<template>
    <!--
      Profile-picture modal — migrated off the Bootstrap modal onto the shared
      BaseModal shell (overlay + box + motion + a11y), mirroring AgreementsModal.
      Two states are views of one modal: an empty picker (with a preview of the
      current photo, if any) and the cropper. The body and footer cross-fade
      between states. Controlled errors (invalid format, crop conversion, API
      business errors) surface inline via PretorFormError; only unexpected
      backend failures fall through to a toast (ErrorService.Resolve). Colors are
      inlined — the BaseModal family is self-contained and inherits no tokens.
    -->
    <BaseModal
        :show="visible"
        @update:show="OnUpdateShow"
        :closable="true"
        :dismissible="!hasFile"
        :ariaLabel="'Cambio de foto de perfil'"
        :maxWidth="480">

        <template #header>
            <span class="pm-head__title">Cambio de foto de perfil</span>
        </template>

        <input ref="fileInput" type="file" accept="image/*" hidden @change="HandleInputChange">

        <transition name="pm-swap" mode="out-in" @enter="OnCropperEnter">

            <!-- PICKER -->
            <div v-if="!hasFile" key="picker" class="pm-body pm-body--picker">
                <!-- Current photo preview, so the user sees what they're replacing. -->
                <div v-if="hasCurrentPicture" class="pm-current">
                    <img :src="currentProfilePicture" alt="Foto de perfil actual" class="pm-current__img">
                    <span class="pm-current__caption">Foto actual</span>
                </div>

                <button type="button"
                    class="pm-dropzone"
                    :class="{ 'pm-dropzone--over': isDragOver }"
                    @click="OpenFilePicker"
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @drop.prevent="HandleDrop">
                    <span class="pm-dropzone__icon">
                        <i class="bi bi-image"></i>
                    </span>
                    <span class="pm-dropzone__title">Selecciona una imagen</span>
                    <span class="pm-dropzone__hint">Arrastra un archivo aquí o haz clic para buscarlo</span>
                    <span class="pm-dropzone__formats">JPG, PNG o HEIC</span>
                </button>

                <PretorFormError class="pm-formError" :message="formError"/>
            </div>

            <!-- CROPPER -->
            <div v-else key="cropper" class="pm-body pm-body--cropper">
                <div class="pm-hint">
                    <i class="bi bi-arrows-move pm-hint__icon"></i>
                    <span>Ajusta y encuadra la imagen. Cuando estés listo, actualiza tu foto de perfil.</span>
                </div>

                <div class="pm-cropperFrame">
                    <Cropper ref="cropper"/>
                </div>

                <PretorFormError class="pm-formError" :message="formError"/>
            </div>
        </transition>

        <template #footer>
            <transition name="pm-swap" mode="out-in">
                <div :key="hasFile ? 'cropper' : 'picker'" class="pm-footer">
                    <!-- PICKER -->
                    <template v-if="!hasFile">
                        <PretorButton
                            variant="outline"
                            :label="'Cancelar'"
                            :Class="'pmFooterBtn'"
                            @click="CloseModal"/>

                        <PretorButton v-if="hasCurrentPicture"
                            :icon="'bi bi-trash3'"
                            :label="'Eliminar foto'"
                            :Class="'pmFooterBtn pmFooterBtn--danger'"
                            @click="DeleteProfilePicture"/>
                    </template>

                    <!-- CROPPER -->
                    <template v-else>
                        <PretorButton
                            variant="outline"
                            :icon="'bi bi-arrow-repeat'"
                            :label="'Elegir otra'"
                            :Class="'pmFooterBtn'"
                            @click="OpenFilePicker"/>

                        <PretorButton
                            :icon="'bi bi-check-circle'"
                            :label="'Actualizar foto'"
                            :Class="'pmFooterBtn'"
                            @click="UpdateProfilePicture"/>
                    </template>
                </div>
            </transition>
        </template>
    </BaseModal>
</template>

<script>
import Cropper from './Cropper.vue';
import PretorButton from '../Generic/Button.vue';
import BaseModal from '@/components/Generic/BaseModal.vue';
import PretorFormError from '@/components/Generic/FormError.vue';
import { useStore } from '@/store/DefaultStore.js';
import UserService from '@/services/UserService.js';
import ShowNotification from '@/services/ToastService.js';
import ErrorService from '@/services/ErrorService.js';

export default {
  name: 'ProfileModal',

  components: {
    Cropper, PretorButton, BaseModal, PretorFormError
  },

  data() {
    return {
      // BaseModal visibility + inline (controlled) error surface
      visible: false,
      formError: '',
      isDragOver: false,

      selectedFile: null,
      pendingFile: null,
      currentProfilePicture: null
    }
  },

  setup() {
    const store = useStore();

    return {
      store
    }
  },

  computed: {
    hasFile() {
      return this.selectedFile != null;
    },
    hasCurrentPicture() {
      return this.currentProfilePicture != null;
    }
  },

  watch: {
    // Reset transient state whenever the modal closes, regardless of how (×,
    // backdrop, Escape, or a successful action).
    visible(isOpen) {
      if (isOpen) return;

      this.selectedFile = null;
      this.pendingFile = null;
      this.formError = '';
      this.isDragOver = false;
    }
  },

  methods: {
    // Parent (Navbar) opens the modal through this — contract preserved.
    OpenModal() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }

      this.currentProfilePicture = this.store.LoggedUser.ProfilePictureURL ? this.store.LoggedUser.ProfilePictureURL : null;
      this.selectedFile = null;
      this.formError = '';

      this.visible = true;
    },

    CloseModal() {
      this.visible = false;
    },

    OnUpdateShow(show) {
      this.visible = show;
    },

    HandleInputChange() {
      this.SetSelectedFile(this.$refs.fileInput.files[0]);
    },

    HandleDrop(event) {
      this.isDragOver = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.SetSelectedFile(file);
    },

    // Shared file intake for both the native picker and drag-and-drop.
    SetSelectedFile(file) {
      this.formError = '';

      this.selectedFile = file ? file : null;
      if (this.selectedFile == null)
        return;

      // Only JPG, PNG and HEIC are accepted — a controlled error surfaces inline.
      if (!['image/jpeg', 'image/png', 'image/heic'].includes(this.selectedFile.type)) {
        this.formError = 'El formato de la imagen no es válido. Usa JPG, PNG o HEIC.';
        this.selectedFile = null;
        return;
      }

      // Hand the file off to the cropper once it's in the DOM. On a picker→cropper
      // swap the <Cropper> mounts only after the leave transition finishes (out-in),
      // so LoadPendingImage no-ops here and OnCropperEnter runs it instead. When
      // already in the cropper (choosing another photo), the ref exists and this
      // $nextTick loads it directly — no swap, so no enter hook fires.
      this.pendingFile = this.selectedFile;
      this.$nextTick(() => {
        this.LoadPendingImage();
      })
    },

    // Fires as the cropper body finishes entering on a picker→cropper swap.
    OnCropperEnter() {
      this.LoadPendingImage();
    },

    LoadPendingImage() {
      if (this.$refs.cropper && this.pendingFile) {
        this.$refs.cropper.uploadImage(this.pendingFile);
        this.pendingFile = null;
      }
    },

    OpenFilePicker() {
      this.$refs.fileInput.click();
    },

    DeleteProfilePicture() {
      this.formError = '';
      this.store.SetLoader(true);
      UserService.DeleteUserProfilePicture()
        .then(result => {

          this.store.LoggedUser.ProfilePictureURL = null;

          if (this.store.loadedUsersData[this.store.LoggedUser.PKUser]) {
            this.store.loadedUsersData[this.store.LoggedUser.PKUser].ProfilePictureURL = null;
            this.store.loadedUsersData[this.store.LoggedUser.PKUser].Initials = result.Initials;
          }

          this.currentProfilePicture = null;

          ShowNotification.Success('La foto de perfil se ha eliminado correctamente');
          this.visible = false;
        })
        .catch(error => {
          const inline = ErrorService.Resolve(error, 'Ocurrió un error al eliminar la foto de perfil.');
          if (inline) { this.formError = inline; }
        })
        .finally(() => {
          this.store.SetLoader(false);
        });
    },

    async UpdateProfilePicture() {
      this.formError = '';

      let imageFile;
      try {
        imageFile = await this.$refs.cropper.getCroppedImage();
      } catch (e) {
        this.formError = 'Ocurrió un error al procesar la imagen. Intenta con otra foto.';
        return;
      }

      this.store.SetLoader(true);

      let formData = new FormData();
      formData.append('Image', imageFile);

      await UserService.UpdateUserProfilePicture(formData)
        .then(result => {
          this.store.LoggedUser.ProfilePictureURL = result.ProfilePictureURL;

          if (this.store.loadedUsersData[this.store.LoggedUser.PKUser]) {
            this.store.loadedUsersData[this.store.LoggedUser.PKUser].ProfilePictureURL = result.ProfilePictureURL;
          }

          ShowNotification.Success('La foto de perfil se ha actualizado correctamente');
          this.visible = false;
        })
        .catch(error => {
          const inline = ErrorService.Resolve(error, 'Ocurrió un error al actualizar la foto de perfil.');
          if (inline) { this.formError = inline; }
        })

      this.store.SetLoader(false);
    },
  }
}
</script>

<style scoped>
/* Self-contained palette (matching the BaseModal family / AgreementsModal):
     ink #1f1f2c · muted #6b6680 · faint #9a94a8 · line #ece8f4
     lavender-soft #f2f4ff · lavender #dee4ff · accent-ink #4b4770
     danger #b3261e · ease-out cubic-bezier(0.23, 1, 0.32, 1) */

/* ---------------- Header ---------------- */
.pm-head__title {
    font-family: 'SwitzerBold', sans-serif;
    font-size: 16px;
    letter-spacing: -0.01em;
    color: #1f1f2c;
}

/* ---------------- Body shell ---------------- */
.pm-body {
    padding: 6px 24px 4px;
}

/* ---------------- Picker ---------------- */
.pm-current {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
}

.pm-current__img {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 1px 2px rgba(9, 9, 14, 0.12), 0 8px 20px -10px rgba(9, 9, 14, 0.35);
}

.pm-current__caption {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    letter-spacing: 0.01em;
    color: #9a94a8;
}

.pm-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 30px 20px;
    border: 1.5px dashed #d9d5e6;
    border-radius: 16px;
    background-color: #faf9fc;
    cursor: pointer;
    text-align: center;
    transition: border-color 200ms cubic-bezier(0.23, 1, 0.32, 1),
        background-color 200ms cubic-bezier(0.23, 1, 0.32, 1),
        transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.pm-dropzone:active {
    transform: scale(0.99);
}

@media (hover: hover) and (pointer: fine) {
    .pm-dropzone:hover {
        border-color: #b9b1d6;
        background-color: #f2f4ff;
    }

    .pm-dropzone:hover .pm-dropzone__icon {
        transform: translateY(-2px);
        background-color: #dee4ff;
    }
}

/* Active drag state — accent border + tinted fill so the target reads clearly. */
.pm-dropzone--over {
    border-color: #4b4770;
    border-style: solid;
    background-color: #f2f4ff;
}

.pm-dropzone__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-bottom: 4px;
    border-radius: 14px;
    background-color: #f2f4ff;
    color: #4b4770;
    font-size: 22px;
    transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1),
        background-color 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.pm-dropzone__icon i {
    line-height: 1;
}

.pm-dropzone__title {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 15px;
    color: #1f1f2c;
}

.pm-dropzone__hint {
    font-family: 'Synonym', sans-serif;
    font-size: 12.5px;
    color: #6b6680;
    line-height: 1.4;
}

.pm-dropzone__formats {
    margin-top: 2px;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 10.5px;
    letter-spacing: 0.04em;
    color: #9a94a8;
}

/* ---------------- Cropper ---------------- */
.pm-hint {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 12px;
    background-color: #f2f4ff;
    color: #4b4770;
    font-size: 12.5px;
    line-height: 1.45;
}

.pm-hint__icon {
    flex-shrink: 0;
    font-size: 14px;
}

.pm-cropperFrame {
    border-radius: 14px;
    overflow: hidden;
    background-color: #1f1f2c;
}

.pm-cropperFrame :deep(.cropper) {
    border-radius: 14px;
}

/* ---------------- Shared error label ---------------- */
.pm-formError {
    margin: 14px 0 4px !important;
}

/* ---------------- Footer ---------------- */
.pm-footer {
    padding: 14px 24px 18px;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    align-items: stretch;
}

.pm-footer > * {
    flex: 1 1 0;
    min-width: 0;
}

:global(.pmFooterBtn) {
    width: 100%;
    margin-top: 0 !important;
}

/* Destructive action — reads as danger without shouting. */
:global(.pmFooterBtn--danger) {
    background-color: #fdecec !important;
    color: #b3261e !important;
}

@media (hover: hover) and (pointer: fine) {
    :global(.pmFooterBtn--danger:hover:not(:disabled)) {
        filter: none !important;
        background-color: #fbdcdc !important;
    }
}

/* ---------------- State cross-fade ---------------- */
.pm-swap-enter-active {
    transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1), transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.pm-swap-leave-active {
    transition: opacity 150ms ease, transform 150ms ease;
}

.pm-swap-enter-from {
    opacity: 0;
    transform: translateY(8px);
}

.pm-swap-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* ---------------- Mobile ---------------- */
@media screen and (max-width: 600px) {
    .pm-body {
        padding: 4px 18px 2px;
    }

    .pm-dropzone {
        padding: 26px 16px;
    }

    .pm-footer {
        padding: 12px 18px 16px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .pm-swap-enter-active {
        transition: opacity 160ms ease;
    }

    .pm-swap-leave-active {
        transition: opacity 120ms ease;
    }

    .pm-swap-enter-from,
    .pm-swap-leave-to {
        transform: none;
    }

    .pm-dropzone,
    .pm-dropzone__icon {
        transition: border-color 200ms ease, background-color 200ms ease;
    }

    .pm-dropzone:active {
        transform: none;
    }
}
</style>
